import { IImpersonationContext, ResourceAutoCompleteViewModelType } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AppImportContactsViewModel } from '../../../viewmodels/AppViewModels';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { Checkbox } from '../../../web/components/Checkbox';
import { AddNewTagFooter } from '../../../web/components/tags/AddNewTagFooter';
import { useAddNewTagLogic } from '../../../web/hooks/useAddNewTagLogic';
import { styleSheet } from './styles';

interface IProps {
	importContacts: AppImportContactsViewModel;
	impersonationContext?: IImpersonationContext;
	showDataRefreshOption?: boolean;
	dataRefresh: boolean;
	onDataRefreshChange: (checked: boolean) => void;
}

const _ContactImportUploadOptions: React.FC<IProps> = props => {
	const { importContacts, impersonationContext, showDataRefreshOption, dataRefresh, onDataRefreshChange } = props;

	const tagInputRef = React.useRef<HTMLInputElement>(null);

	const { handleAddNewTag, onTagSearchFieldRef } = useAddNewTagLogic({
		onTagSelected: tagName => {
			importContacts.options.setTags([tagName]);
		},
	});

	const onRenderResultsFooter = React.useCallback(
		(searchQuery: string, _renderedItems: unknown[], totalCount: number) => {
			if (totalCount === 0 && searchQuery) {
				return <AddNewTagFooter searchQuery={searchQuery} onAddTag={handleAddNewTag} />;
			}
			return null;
		},
		[handleAddNewTag]
	);

	const clearSelectedTag = React.useCallback(() => {
		importContacts.options.setTags([]);
	}, [importContacts]);

	const onSearchFieldInputKeyDown = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
			if (!!e.sourceEvent && e.sourceEvent.keyCode === 13) {
				(e.sourceEvent.target as HTMLInputElement).blur();
			}
		},
		[]
	);

	const onTagSelected = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
			const selection = e.selection;
			const tag = typeof selection === 'string' ? selection : '';
			if (e.target) {
				e.target.setSearchQuery(tag);
			}
			importContacts.options.setTags(tag ? [tag] : []);
		},
		[importContacts]
	);

	const onTagInputChanged = React.useCallback(
		(e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
			if (e.sourceEvent) {
				const tag = (e.sourceEvent.target as HTMLInputElement).value;
				importContacts.options.setTags(tag ? [tag] : []);
			}
		},
		[importContacts]
	);

	const onDataRefreshChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onDataRefreshChange(e.target.checked);
		},
		[onDataRefreshChange]
	);

	return (
		<div className={css(styleSheet.pickFileOptions)}>
			<div className={css(styleSheet.tagOptionsBlock)}>
				<div className={css(styleSheet.optionsTitle)}>Preferred Tag:</div>
				<div className={css(styleSheet.optionsSubtitle)}>You can add a tag to everyone you import.</div>
				<SimpleAutoCompleteSearchField
					impersonationContext={impersonationContext}
					onChange={onTagInputChanged}
					onItemSelected={onTagSelected}
					onKeyDown={onSearchFieldInputKeyDown}
					onClear={clearSelectedTag}
					onRenderResultsFooter={onRenderResultsFooter}
					onSearchFieldRef={onTagSearchFieldRef}
					onInputRef={tagInputRef}
					pageSize={10}
					placeholder='Search existing or enter new tag'
					renderNoResultsItem={false}
					resultsLimit={10}
					style={styleSheet.tagSearchField}
					type={ResourceAutoCompleteViewModelType.Tag}
				/>
			</div>
			{showDataRefreshOption ? (
				<div className={css(styleSheet.dataRefreshRow)}>
					<Checkbox id='contact-import-data-refresh' onChange={onDataRefreshChanged} checked={dataRefresh}>
						<div>This is a data refresh (clear renewal keyfacts and policy tags)</div>
					</Checkbox>
				</div>
			) : null}
		</div>
	);
};

export const ContactImportUploadOptions = observer(_ContactImportUploadOptions);
